import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, DollarSign, Zap, TrendingUp, Activity } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useCompany } from "@/contexts/CompanyContext";
import { useAuth } from "@/_core/hooks/useAuth";

export default function AIUsage() {
  const { selectedCompanyId } = useCompany();
  const { isAuthenticated } = useAuth();

  // Date range state (default to current month)
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(1); // First day of month
    return date.toISOString().split("T")[0];
  });

  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    return date.toISOString().split("T")[0];
  });

  // Fetch AI usage data
  const {
    data: summary,
    isLoading: summaryLoading,
    refetch: refetchSummary,
  } = trpc.aiUsage.getSummary.useQuery(
    {
      companyId: selectedCompanyId || 0,
      startDate: new Date(startDate).getTime(),
      endDate: new Date(endDate).getTime(),
    },
    {
      enabled: isAuthenticated && !!selectedCompanyId,
    }
  );

  const { data: byFeature, isLoading: featureLoading } =
    trpc.aiUsage.getByFeature.useQuery(
      {
        companyId: selectedCompanyId || 0,
        startDate: new Date(startDate).getTime(),
        endDate: new Date(endDate).getTime(),
      },
      {
        enabled: isAuthenticated && !!selectedCompanyId,
      }
    );

  const { data: byProvider, isLoading: providerLoading } =
    trpc.aiUsage.getByProvider.useQuery(
      {
        companyId: selectedCompanyId || 0,
        startDate: new Date(startDate).getTime(),
        endDate: new Date(endDate).getTime(),
      },
      {
        enabled: isAuthenticated && !!selectedCompanyId,
      }
    );

  const { data: recentLogs, isLoading: logsLoading } =
    trpc.aiUsage.getRecentLogs.useQuery(
      {
        companyId: selectedCompanyId || 0,
        limit: 20,
      },
      {
        enabled: isAuthenticated && !!selectedCompanyId,
      }
    );

  // Format currency (costs are in cents)
  const formatCurrency = (cents: number) => {
    const amount = cents / 100;
    return new Intl.NumberFormat("de-CH", {
      style: "currency",
      currency: "CHF",
    }).format(amount);
  };

  // Format number with thousands separators
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat("de-CH").format(num);
  };

  // Format date/time
  const formatDateTime = (timestamp: Date | string) => {
    return new Date(timestamp).toLocaleString("de-CH", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleRefresh = () => {
    refetchSummary();
  };

  const isLoading = summaryLoading || featureLoading || providerLoading;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold">AI Usage & Costs</h1>
          <p className="text-muted-foreground mt-2">
            Track AI/LLM token usage and costs across all features
          </p>
        </div>

        {/* Date Range Filter */}
        <Card>
          <CardHeader>
            <CardTitle>Reporting Period</CardTitle>
            <CardDescription>
              Select the date range for your AI usage report
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4 items-end">
              <div className="flex-1 space-y-2">
                <Label htmlFor="startDate">Start Date</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                />
              </div>
              <div className="flex-1 space-y-2">
                <Label htmlFor="endDate">End Date</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                />
              </div>
              <Button onClick={handleRefresh} disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        {summary && (
          <div className="grid gap-4 md:grid-cols-4">
            {/* Total Cost */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Cost
                </CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCurrency(summary.totalCost)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  AI/LLM API costs
                </p>
              </CardContent>
            </Card>

            {/* Total Calls */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">API Calls</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatNumber(summary.totalCalls)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Total requests
                </p>
              </CardContent>
            </Card>

            {/* Total Tokens */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Tokens
                </CardTitle>
                <Zap className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatNumber(summary.totalTokens)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Input + output tokens
                </p>
              </CardContent>
            </Card>

            {/* Avg Cost per Call */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Avg Cost/Call
                </CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {summary.totalCalls > 0
                    ? formatCurrency(
                        Math.round(summary.totalCost / summary.totalCalls)
                      )
                    : formatCurrency(0)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Per API request
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Usage by Feature */}
        {byFeature && byFeature.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Usage by Feature</CardTitle>
              <CardDescription>
                AI usage breakdown by application feature
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Feature</TableHead>
                    <TableHead className="text-right">API Calls</TableHead>
                    <TableHead className="text-right">Tokens</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {byFeature.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">
                        {item.feature
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, c => c.toUpperCase())}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumber(item.callCount)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumber(item.totalTokens)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(item.totalCost)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-muted/50 font-bold">
                    <TableCell>Total</TableCell>
                    <TableCell className="text-right">
                      {formatNumber(
                        byFeature.reduce((sum, item) => sum + item.callCount, 0)
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatNumber(
                        byFeature.reduce(
                          (sum, item) => sum + item.totalTokens,
                          0
                        )
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(
                        byFeature.reduce((sum, item) => sum + item.totalCost, 0)
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Usage by Provider */}
        {byProvider && byProvider.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Usage by Provider & Model</CardTitle>
              <CardDescription>
                AI usage breakdown by provider and model
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Provider</TableHead>
                    <TableHead>Model</TableHead>
                    <TableHead className="text-right">API Calls</TableHead>
                    <TableHead className="text-right">Tokens</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {byProvider.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium capitalize">
                        {item.provider}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {item.model}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumber(item.callCount)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumber(item.totalTokens)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(item.totalCost)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-muted/50 font-bold">
                    <TableCell colSpan={2}>Total</TableCell>
                    <TableCell className="text-right">
                      {formatNumber(
                        byProvider.reduce(
                          (sum, item) => sum + item.callCount,
                          0
                        )
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatNumber(
                        byProvider.reduce(
                          (sum, item) => sum + item.totalTokens,
                          0
                        )
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(
                        byProvider.reduce(
                          (sum, item) => sum + item.totalCost,
                          0
                        )
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Recent Logs */}
        {recentLogs && recentLogs.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Recent API Calls</CardTitle>
              <CardDescription>Last 20 AI/LLM API requests</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="relative overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Timestamp</TableHead>
                      <TableHead>Feature</TableHead>
                      <TableHead>Model</TableHead>
                      <TableHead className="text-right">Tokens</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                      <TableHead className="text-right">Time (ms)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recentLogs.map(log => (
                      <TableRow key={log.id}>
                        <TableCell className="text-xs">
                          {formatDateTime(log.timestamp)}
                        </TableCell>
                        <TableCell className="text-xs">
                          {log.feature.replace(/_/g, " ")}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {log.model}
                        </TableCell>
                        <TableCell className="text-right text-xs">
                          {formatNumber(log.totalTokens)}
                        </TableCell>
                        <TableCell className="text-right text-xs">
                          {formatCurrency(log.totalCost)}
                        </TableCell>
                        <TableCell className="text-right text-xs">
                          {log.responseTime
                            ? formatNumber(log.responseTime)
                            : "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {summary && summary.totalCalls === 0 && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Activity className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No AI usage data</h3>
              <p className="text-muted-foreground text-center max-w-md">
                No AI/LLM API calls were made during the selected period.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Loading State */}
        {isLoading && (
          <Card>
            <CardContent className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
                <p className="text-muted-foreground">
                  Loading AI usage data...
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
